import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
  Grid,
  Box,
} from "@mui/material";
import useDataFetchFunction from "hooks/useDataFetchFunction";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import AppConfig from "api/AppConfig";
import axios from "axios";
import SubCard from "ui-component/cards/SubCard";
import ManageSubscription from "./ManageSubscription";
import { getCustomerPortalUrl, getManagePaymentMethodsUrl, getSubscriptionDetailsByCustomerId } from "api/hiredrateURLConstruct";
import { useNotification } from "store/context/Notification";
import { useState } from "react";
import { ERROR } from "store/constant";
import CheckoutBackdrop from "ui-component/loader/CheckoutBackdrop";
import { useTheme } from "@mui/styles";
import HDConfirmationDialog from "ui-component/dialog/HDConfirmationDialog";

const Subdetails = () => {

  const { companyID, customerId } = useSelector((state) => state.account);
  const configObject = AppConfig("APIConfig");

  const limits = [
    { name: "Users", remaining: 10, limit: 10 },
    { name: "Job Positions", remaining: 15, limit: 15 },
    { name: "Applications", remaining: 30, limit: 30 },
    { name: "Match Analysis", remaining: 9, limit: 9 },
  ];
  const theme = useTheme()
  const [
    subscriptionDetails,
    subscriptionDetailsError,
    subscriptiondetails,
    fetchSubscribtionDetails,
  ] = useDataFetchFunction()

  const getSubscriptionDetails = (customerId) => {
    fetchSubscribtionDetails({
      axiosInstance: axios,
      method: "get",
      url: getSubscriptionDetailsByCustomerId(customerId),
      requestConfig: configObject,
    });
  };

  useEffect(() => {
    getSubscriptionDetails(customerId);
  }, [customerId])

  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showBackdrop, setShowBackdrop] = useState(false);

  const handleManageBilling = async () => {
    try {
      setLoading(true);
      setShowBackdrop(true);

      const response = await axios.post(
        getCustomerPortalUrl(),
        {
          company_id: companyID,
          redirect_url: `${window.location.origin}/settings/subscription`,
        },
        {
          ...configObject,
          headers: {
            ...configObject?.headers,
            "Content-Type": "application/json",
          },
        }

      );

      if (response.data?.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        "Something went wrong";

      showNotification(
        ERROR,
        message,
        ERROR,
        "Subscription"
      );
      setShowBackdrop(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle manage billing click - show confirmation first
  const handleManageBillingClick = () => {
    setShowConfirmDialog(true);
  };

  // Handle confirmation
  const handleConfirmManageBilling = () => {
    setShowConfirmDialog(false);
    handleManageBilling();
  };

  // Handle cancel
  const handleCancelManageBilling = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      {/* Confirmation Dialog */}
      <HDConfirmationDialog
        open={showConfirmDialog}
        onClose={handleCancelManageBilling}
        onConfirm={handleConfirmManageBilling}
        title="Manage Billing & Account"
        message="You will be redirected to the customer portal where you can manage your billing information, payment methods, invoices, and other account settings. Do you want to continue?"
        confirmButtonText="Proceed to Portal"
        cancelButtonText="Cancel"
        loading={loading}
      >
        <Box sx={{
          mt: 2,
          p: 2,
          bgcolor: theme.palette.grey[50],
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You can manage:
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              • Billing information
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              • Payment methods
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              • Company details
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              • Invoices & billing history
            </Typography>
          </Box>

          <Box sx={{
            mt: 2,
            p: 1.5,
            bgcolor: theme.palette.warning.light + '20',
            borderRadius: 1,
            border: `1px solid ${theme.palette.warning.light}`
          }}>
            <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
              You will be redirected to the customer portal to manage your account.
            </Typography>
          </Box>
        </Box>
      </HDConfirmationDialog>

      {/* Backdrop for loading state */}
      <CheckoutBackdrop
        open={showBackdrop}
        title="Redirecting to customer portal..."
        description="Please wait while we prepare your secure portal session."
        selectedItem="Customer Portal"
        selectedItemLabel="Redirecting to"
        redirectText="Redirecting to customer portal..."
        providerName="Customer Portal"
      />

      <Grid container spacing={2}>

        <Grid item xs={12} md={3}>
          <Stack>
            <SubCard
              title="Subscription Details"
              sx={{ width: "100%", height: "100%" }}
            >
              <Stack spacing={2} sx={{ px: 2, py: 1 }}>
                {/* Subscription Plan */}
                <Stack direction="row" alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      minWidth: 160,
                    }}
                  >
                    Subscription Plan
                  </Typography>
                  <Typography sx={{ mx: 1, fontWeight: 600 }}>
                    :
                  </Typography>
                  <Typography variant="hdBody1">
                    {/* {subscriptionDetails?.subscription_details?.credit_block?.block_type}\ */}
                    {subscriptionDetails?.subscription_details?.subscription_info?.plan_name}
                  </Typography>
                </Stack>

                {/* Is Trial */}
                <Stack direction="row" alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      minWidth: 160,
                    }}
                  >
                    Is Trial
                  </Typography>
                  <Typography sx={{ mx: 1, fontWeight: 600 }}>
                    :
                  </Typography>
                  <Typography variant="hdBody1">
                    {/* {subscriptionDetails?.subscription_details?.credit_block?.block_type} */}
                    {subscriptionDetails?.subscription_details?.subscription_info?.is_trial
                        ? "Yes"
                        : "No"}
                  </Typography>
                </Stack>

                {/* Start Date */}
                <Stack direction="row" alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      minWidth: 160,
                    }}
                  >
                    Start Date
                  </Typography>
                  <Typography sx={{ mx: 1, fontWeight: 600 }}>
                    :
                  </Typography>
                  <Typography variant="hdBody1">
                    {subscriptionDetails?.subscription_details?.credit_block?.date_valid_from}
                  </Typography>
                </Stack>

                {/* End Date */}
                <Stack direction="row" alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      minWidth: 160,
                    }}
                  >
                    End Date
                  </Typography>
                  <Typography sx={{ mx: 1, fontWeight: 600 }}>
                    :
                  </Typography>
                  <Typography variant="hdBody1">
                    {subscriptionDetails?.subscription_details?.credit_block?.date_valid_to}
                  </Typography>
                </Stack>

                {subscriptionDetails?.subscription_details?.credit_block?.block_type?.toUpperCase() === "TRIAL" && (
                  <Stack alignItems="center">
                    <ManageSubscription />
                  </Stack>
                )}
              </Stack>
            </SubCard>
          </Stack>
        </Grid>

        <Grid item xs={12} md={2}>
          <SubCard title="Billing & Account Management">
            <Stack spacing={2} sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Manage your billing information, payment methods, company details,
                invoices, and other customer account settings.
              </Typography>

              <Button
                variant="contained"
                onClick={handleManageBillingClick}
                disabled={loading}
                fullWidth
              >
                {loading ? "Redirecting..." : "Manage Customer Details"}
              </Button>
            </Stack>
          </SubCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SubCard title="Subscription Limits" sx={{ width: "100%" }}>
            <Stack direction="column" spacing={0.1} sx={{ px: 3 }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      textDecoration: "underline",
                    }}
                  >
                    #
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      textDecoration: "underline",
                    }}
                  >
                    Used Limit
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      textDecoration: "underline",
                    }}
                  >
                    Max Limit
                  </Typography>
                </Grid>
              </Grid>

              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>
                    Job Positions
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {
                      subscriptionDetails?.subscription_details?.limits
                        ?.job_position_limit?.active_limit
                    }
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {
                      subscriptionDetails?.subscription_details?.limits
                        ?.job_position_limit?.max_limit
                    }
                  </Typography>
                </Grid>
              </Grid>

              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>
                    Applications
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {
                      subscriptionDetails?.subscription_details?.limits
                        ?.application_limit?.active_limit
                    }
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {
                      subscriptionDetails?.subscription_details?.limits
                        ?.application_limit?.max_limit
                    }
                  </Typography>
                </Grid>
              </Grid>

              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>
                    Match Analysis
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {
                      subscriptionDetails?.subscription_details?.limits
                        ?.match_analysis_limit?.active_limit
                    }
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {
                      subscriptionDetails?.subscription_details?.limits
                        ?.match_analysis_limit?.max_limit
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Stack>
          </SubCard>
        </Grid>

      </Grid>
    </>
  );
};

export default Subdetails;